import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import DatePicker, { DatePickerProps } from 'react-date-picker';
import Theme from "../constants/Theme";
import caretDown from '../assets/arrowDown.svg'
import {DPIconEyeClose, DPIconEyeOpen} from "../assets/icons";

interface InputBaseProps {
  loading?: boolean;
  label?: string;
  error?: string;
  suffix?: React.ReactElement;
  icon?: React.ReactElement;
  suffixClick?: () => void;
}

interface InputProps extends InputBaseProps {
  value?: string;
  bgColor?: string;
}


interface SelectProps extends InputBaseProps {
  id?: string;
  value?: { label: string; value: string } | string;
  options: { label: string; value: string; isDisabled?: boolean }[];
  className?: string;
  onChange?: (res: { target: { name: string; value: { label: string; value: string } } }) => void;
  style?: object;
  name: string;
  placeholder?: string;
  bgColor?: string;
}

type PasswordComponent = React.FC<InputProps & React.InputHTMLAttributes<any>>;
type SelectComponent = React.FC<SelectProps>;
type DateComponent = React.FC<{ onChange?: any } & InputBaseProps & DatePickerProps & React.InputHTMLAttributes<any>>;
type InputComponent = React.FC<InputProps & React.InputHTMLAttributes<any>> & {
  Password: PasswordComponent;
  Select: SelectComponent;
  Date: DateComponent;
};

const Input: InputComponent = (props) => {
  const { label, name, id, error, suffix, className, icon, bgColor, value, ...rest } = props;

  return (
    <InputStyle className={className} error={error} suffix={suffix} icon={icon} bgColor={bgColor}>
      {label && (
        <label className="input--label" htmlFor={name || id}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        {icon && <span className="input--input_prefix">{icon}</span>}
        <input id={name || id} name={name} {...rest} className="input--input" />
        {suffix && <span className="input--input_suffix">{suffix}</span>}
      </div>
      {error && <small className="input--input_error">{error}</small>}
    </InputStyle>
  );
};

const InputPassword: PasswordComponent = (props) => {
  const [show, setShow] = useState(false);
  const toggleShow = () => setShow(!show);
  const onchange = (e: any) => {
    const { value } = e.target;
    if (!value) setShow(false);
    if (props.onChange) props.onChange(e);
  };
  return (
    <Input
      {...props}
      onChange={onchange}
      type={show ? 'text' : 'password'}
      suffix={
        <span onClick={toggleShow} aria-hidden="true">
          {!show ? <DPIconEyeOpen /> : <DPIconEyeClose />}
        </span>
      }
    />
  );
};

const InputSelect: SelectComponent = ({ options, placeholder, suffix, icon, id, label, name, onChange, error, className, bgColor }) => {
  const onSelectChange = (e: any) => {
    const { value, options: op } = e.target;
    if (onChange) {
      onChange({
        target: {
          name,
          value: {
            label: options[op.selectedIndex - 1].label,
            value,
          },
        },
      });
    }
  };

  return (
    <InputStyle className={className} error={error} suffix={suffix} icon={icon} bgColor={bgColor}>
      {label && (
        <label className="input--label" htmlFor={name || id}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative', background: 'white', borderRadius: 5 }}>
        {icon && <span className="input--input_prefix">{icon}</span>}
        <select
          name={name}
          id={name || id}
          onChange={onSelectChange}
          defaultValue="DEFAULT"
          style={
            !suffix
              ? {
                  background: `url(${caretDown}) no-repeat ${bgColor || ''}`,
                  backgroundPosition: 'calc(100% - 22px) 50%',
                  paddingRight: '60px',
                }
              : {}
          }
          placeholder={placeholder}
          className="input--input"
        >
          {placeholder && (
            <option value="DEFAULT" disabled>
              {placeholder}
            </option>
          )}
          {options.map(({ value: v, label: l }, i) => (
            <option key={`${v}-${i.toString()}`} value={v}>
              {l}
            </option>
          ))}
        </select>
        {suffix && <span className="input--input_suffix">{suffix}</span>}
      </div>
      {error && <small className="input--input_error">{error}</small>}
    </InputStyle>
  );
};

const InputDate: DateComponent = ({ onChange, label, error, value, name, id, className, ...rest }) => {
  const handleChange = (date: Date) => onChange && onChange({ target: { name, value: date } });
  return (
    <InputStyle error={error} className={className}>
      {label && (
        <label className="input--label" htmlFor={name || id}>
          {label}
        </label>
      )}
      <DatePicker name={name} value={value} onChange={handleChange} {...rest} />
      {error && <small className="input--input_error">{error}</small>}
    </InputStyle>
  );
};

Input.Password = InputPassword;
Input.Select = InputSelect;
Input.Date = InputDate;

const InputStyle = styled.span<InputProps>`
  position: relative;
  display: block;
  width: 100%;

  .input {
    &--label {
      display: inline-block;
      text-transform: capitalize;
      margin-bottom: 5px;
      font-size: ${Theme.fontSizes.medium};
      font-weight: 400;
      color: #4A4A4A;
    }

    &--input {
      height: 50px;
      width: 100%;
      padding: 0 15px;

      ${({ bgColor }) =>
        bgColor &&
        css`
          background: ${bgColor};
        `}
      
      ${({ suffix }) =>
        suffix &&
        css`
          padding-right: 60px;
        `};
      ${({ icon }) =>
        icon &&
        css`
          padding-left: 60px;
        `};
      border-radius: 5px;
      font-size: ${Theme.fontSizes.large};
      border: 0.65px solid ${Theme.colorPalette.lighterGrey};
      &:focus {
        border: 1px solid ${Theme.colorPalette.darkBlue};
      }
      ${({ error }) =>
        error &&
        css`
          border: 1px solid ${Theme.colorPalette.error};
        `};
      &_error {
        display: inline-block;
        color: ${Theme.colorPalette.error};
        margin-top: 8px;
      }
      &_bottom_label {
        text-align: right;
      }
      &_suffix,
      &_prefix {
        cursor: pointer;
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 50px;
      }
      &_suffix {
        right: 2px;
      }
      &_prefix {
        left: 2px;
      }

      svg path {
        ${({ error }) => (error ? `stroke: ${Theme.colorPalette.error}` : `${Theme.colorPalette.lightGrey}`)}
      }
    }
  }

  .react-date-picker {
    width: 100%;
    &__wrapper {
      height: 50px;
      width: 100%;
      padding: 0 15px;
      border-radius: 5px;
      font-size: ${Theme.fontSizes.large};
      border: 0.65px solid ${Theme.colorPalette.lighterGrey};
      background-color: white;
      ${({ error }) =>
        error &&
        css`
          border: 1px solid ${Theme.colorPalette.error};
        `};
    }

    &__inputGroup__input {
      outline: none;
    }
    &__inputGroup__input:invalid {
      background-color: unset;
    }

    &__inputGroup__divider,
    &__calendar-button__icon,
    &__clear-button__icon {
      stroke: ${Theme.colorPalette.darkBlue};
      color: ${Theme.colorPalette.darkBlue};
    }
  }
`;

export default Input;
